import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { USERS } from "@/constants/testIds";
import { toast } from "sonner";
import { Search, RefreshCw, Zap, ChevronRight, Download, Upload, X, ArrowUpDown, ArrowUp, ArrowDown, BellOff, Clock3, Clock, Flame, MessageCircle, Mail, PowerOff, Tag, StickyNote, AlertTriangle, Users2, Check, Send, MessageSquareReply, Copy } from "lucide-react";
import HintIcon from "@/components/HintIcon";
import ImportUsersModal from "@/components/ImportUsersModal";

function formatCountdown(seconds) {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds <= 0) return "any moment";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

const BLOCKED_LABELS = {
  global_kill_switch: "AI globally off",
  cadence_disabled: "Cadence off",
  dnd: "DND",
  ai_paused: "AI paused",
  cancelled: "Cancelled",
  no_cadence: "No cadence",
  cadence_complete: "Cadence done",
  invalid_join_date: "No join date",
};

// v29.15 — Compact copy-to-clipboard chip for user IDs in table rows.
// Click copies. Truncated for space; hover shows full value.
function IdChip({ label, value, testId }) {
  if (!value) {
    return (
      <span
        data-testid={testId}
        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-slate-400 border border-dashed border-slate-200 rounded px-1.5 py-0.5"
        title={`${label} ID not available`}
      >
        {label}
      </span>
    );
  }
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(String(value));
        toast.success(`${label} ID copied`);
      }}
      title={`${label}: ${value} (click to copy)`}
      className="group inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest bg-slate-100 hover:bg-orange-100 text-slate-700 rounded px-1.5 py-0.5 border border-slate-200 hover:border-orange-300 transition-colors duration-150"
    >
      <span className="text-slate-500 group-hover:text-orange-600">{label}</span>
      <span className="text-slate-800 group-hover:text-orange-700">
        {String(value).length > 10 ? `${String(value).slice(0, 10)}…` : value}
      </span>
      <Copy className="h-2 w-2 text-slate-400 group-hover:text-orange-600" />
    </button>
  );
}

function NextActionPill({ na }) {
  if (!na) return <span className="text-[10px] text-slate-400">—</span>;
  if (na.blocked) {
    const label = BLOCKED_LABELS[na.reason] || "Blocked";
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200"
        title={na.reason_label || label}
      >
        <PowerOff className="h-2.5 w-2.5" /> {label}
      </span>
    );
  }
  const ChannelIcon = na.channel === "email" ? Mail : MessageCircle;
  const secs = na.scheduled_at
    ? Math.round((new Date(na.scheduled_at) - new Date()) / 1000)
    : na.seconds_until;
  const late = secs !== null && secs < 0;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border tabular ${
        late
          ? "bg-orange-50 text-orange-700 border-orange-200"
          : "bg-indigo-50 text-indigo-700 border-indigo-200"
      }`}
      title={na.reason_label}
    >
      <Clock3 className="h-2.5 w-2.5" />
      {late ? "next tick" : `in ${formatCountdown(secs)}`}
      <ChannelIcon className="h-2.5 w-2.5 opacity-70" />
    </span>
  );
}

const STATUS_COLORS = {
  trial: "bg-orange-50 text-orange-700 border-orange-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expired: "bg-slate-100 text-slate-600 border-slate-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

const STATE_LABELS = {
  active_trial: "Active in trial",
  inactive_trial: "Inactive in trial",
  paid: "Subscribed",
  expired: "Trial expired (no pay)",
  cancelled: "Unsubscribed",
};

const BUCKET_LABELS = {
  day1: "Day 1 (0-24h)",
  day2: "Day 2 (24-48h)",
  day3: "Day 3 (48-72h)",
  active: "Active (Subscribed)",
  inactive: "Inactive (trial elapsed, unpaid)",
};

// Auto-generated tags produced by the import wizard. We surface them as
// dedicated quick-filter chips AND deprioritize them below manual tags in the
// row display so the operator sees their intentional labels first.
const AUTO_TAG_LITERALS = new Set(["imported", "phone_incomplete", "exam_unknown"]);
const AUTO_TAG_PREFIXES = ["import_batch_"];
function isAutoTag(t) {
  return AUTO_TAG_LITERALS.has(t) || AUTO_TAG_PREFIXES.some((p) => t.startsWith(p));
}
/** Sort so manual (executive-chosen) tags come first, auto/batch tags trail. */
function orderTagsForDisplay(tags) {
  return [...(tags || [])].sort((a, b) => {
    const aAuto = isAutoTag(a);
    const bAuto = isAutoTag(b);
    if (aAuto === bAuto) return a.localeCompare(b);
    return aAuto ? 1 : -1;
  });
}

const SORT_ACCESSORS = {
  name: (u) => (u.name || "").toLowerCase(),
  exam: (u) => (u.exam || "").toLowerCase(),
  city: (u) => (u.city || "").toLowerCase(),
  trial_hours_left: (u) => u.trial_hours_left ?? -1,
  conversion_score: (u) => u.conversion_score ?? -1,
  user_state: (u) => u.user_state || u.subscription_status || "",
  join_date: (u) => u.join_date || "",
  last_login: (u) => u.last_login || "",
};

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { year: "2-digit", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}
function relative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function SortHeader({ label, field, sort, setSort, hint }) {
  const active = sort.field === field;
  const dir = active ? sort.dir : "asc";
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  const click = () => {
    if (active) setSort({ field, dir: dir === "asc" ? "desc" : "asc" });
    else setSort({ field, dir: "asc" });
  };
  return (
    <th
      className="text-left px-4 py-3 font-semibold cursor-pointer select-none hover:bg-slate-100"
      onClick={click}
      title={hint || `Sort by ${label}`}
      data-testid={`users-sort-${field}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon className={`h-3 w-3 ${active ? "text-slate-800" : "text-slate-400"}`} />
      </span>
    </th>
  );
}

export default function Users() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "");
  const [stateFilter, setStateFilter] = useState(params.get("state") || "");
  const bucket = params.get("bucket") || "";
  const tag = params.get("tag") || "";
  const promo = params.get("promo") || "";
  const escalated = params.get("escalated") === "true";
  const dnd = params.get("dnd") === "true";
  const aiPaused = params.get("ai_paused") === "true";
  const liveSalesMode = params.get("live_sales_mode") === "true";
  const hasFollowup = params.get("has_followup") === "true";
  const city = params.get("city") || "";
  const region = params.get("region") || "";
  const groupFilter = params.get("group") || "";  // v29.14 — filter by group id
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [sort, setSort] = useState({ field: "join_date", dir: "desc" });
  const [nextActions, setNextActions] = useState({}); // {user_id: NextAction}
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [groupsByUser, setGroupsByUser] = useState({}); // {user_id: [group]}
  const [msgCounts, setMsgCounts] = useState({}); // v29.3 — {user_id: {sent, received}}
  const [allGroups, setAllGroups] = useState([]);
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [showRemovePicker, setShowRemovePicker] = useState(false);  // v29.14
  const [assigning, setAssigning] = useState(false);
  const [conflictDialog, setConflictDialog] = useState(null);       // v29.14

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (statusFilter) p.set("status", statusFilter);
      if (stateFilter) p.set("state", stateFilter);
      if (bucket) p.set("bucket", bucket);
      if (tag) p.set("tag", tag);
      if (promo) p.set("promo", promo);
      if (escalated) p.set("escalated", "true");
      if (dnd) p.set("dnd", "true");
      if (aiPaused) p.set("ai_paused", "true");
      if (liveSalesMode) p.set("live_sales_mode", "true");
      if (hasFollowup) p.set("has_followup", "true");
      if (city) p.set("city", city);
      if (region) p.set("region", region);
      if (groupFilter) p.set("group", groupFilter);   // v29.14
      p.set("limit", "500");
      const { data } = await api.get(`/trial-users?${p}`);
      setUsers(data);
      // Bulk-fetch next actions for the visible users (no fatal if it fails)
      if (data.length) {
        try {
          const ids = data.map((u) => u.id).slice(0, 500);
          const { data: nas } = await api.post("/trial-users/next-actions", { ids });
          setNextActions(nas || {});
        } catch { /* non-critical */ }
        // Bulk-fetch group memberships for group-chip rendering
        try {
          const ids = data.map((u) => u.id).slice(0, 500);
          const { data: gm } = await api.post("/groups/user-memberships", { user_ids: ids });
          setGroupsByUser(gm?.memberships || {});
        } catch { setGroupsByUser({}); }
        // v29.3 — Bulk-fetch outbound/inbound message counts for the counts column
        try {
          const ids = data.map((u) => u.id).slice(0, 500);
          const { data: mc } = await api.post("/trial-users/message-counts", { ids });
          setMsgCounts(mc?.counts || {});
        } catch { setMsgCounts({}); }
      } else {
        setNextActions({});
        setGroupsByUser({});
        setMsgCounts({});
      }
    } finally {
      setLoading(false);
    }
  };

  // Local 15s tick so countdowns tick without hammering the server
  useEffect(() => {
    const iv = setInterval(() => setNextActions((prev) => ({ ...prev })), 15000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, statusFilter, stateFilter, bucket, tag, promo, escalated, dnd, aiPaused, liveSalesMode, hasFollowup, city, region, groupFilter]);

  // Load all groups once — used by the "Add to group" picker in the bulk bar.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/groups");
        setAllGroups(data || []);
      } catch { /* non-critical */ }
    })();
  }, []);

  // Reset selection when the filter set changes (rows may disappear)
  useEffect(() => { setSelectedIds(new Set()); }, [q, statusFilter, stateFilter, bucket, tag, escalated, dnd, aiPaused, liveSalesMode, hasFollowup, city, region, groupFilter]);

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAllVisible = (allSelected) => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(sorted.map((u) => u.id)));
  };

  const _performAddOrMove = async (groupId, groupName, mode, userIdsOverride = null) => {
    const targetIds = userIdsOverride || Array.from(selectedIds);
    setAssigning(true);
    try {
      if (!targetIds.length) {
        toast.info("No users to add — all selected users were skipped due to existing memberships.");
        setShowGroupPicker(false);
        setConflictDialog(null);
        setSelectedIds(new Set());
        return;
      }
      const { data } = await api.post(`/groups/${groupId}/members/add`, {
        user_ids: targetIds,
        mode,
      });
      const skipped = Array.from(selectedIds).length - targetIds.length;
      if (mode === "move") {
        const detached = data.detached || [];
        const detachedCount = detached.reduce((n, d) => n + (d.count || 0), 0);
        toast.success(
          `Moved ${data.added} user(s) to "${groupName}"` +
          (detachedCount ? ` · removed from ${detachedCount} other membership(s)` : "")
        );
      } else if (skipped > 0) {
        toast.success(
          `Added ${data.added} user(s) to "${groupName}"` +
          ` · ${skipped} skipped (already in another group)`
        );
      } else {
        toast.success(`Added ${data.added} user(s) to "${groupName}"`);
      }
      setShowGroupPicker(false);
      setConflictDialog(null);
      setSelectedIds(new Set());
      try {
        const ids = users.map((u) => u.id);
        const { data: gm } = await api.post("/groups/user-memberships", { user_ids: ids });
        setGroupsByUser(gm?.memberships || {});
      } catch { /* non-critical */ }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : Array.isArray(d) ? d.map((x) => x?.msg || JSON.stringify(x)).join(" · ")
        : (d && typeof d === "object" ? (d.msg || JSON.stringify(d)) : (e?.message || "Add failed"));
      toast.error(msg);
    } finally { setAssigning(false); }
  };

  const addSelectedToGroup = async (groupId) => {
    const staticGroups = allGroups.filter((g) => !g.auto);
    const grp = staticGroups.find((g) => g.id === groupId);
    if (!grp) return toast.error("Group not found");
    setAssigning(true);
    try {
      // v29.14 — Check for existing memberships FIRST. Static + dynamic.
      const { data: preview } = await api.post(
        `/groups/${groupId}/members/preview-conflicts`,
        { user_ids: Array.from(selectedIds) },
      );
      const conflicts = preview.conflicts || [];
      if (conflicts.length === 0) {
        setAssigning(false);
        return _performAddOrMove(groupId, grp.name, "add");
      }
      setAssigning(false);
      setShowGroupPicker(false);
      setConflictDialog({
        groupId,
        groupName: grp.name,
        conflicts,
        cleanUserIds: preview.clean_user_ids || [],
      });
    } catch (e) {
      setAssigning(false);
      toast.error(e?.response?.data?.detail || "Could not preview conflicts");
    }
  };

  const removeSelectedFromGroup = async (groupId) => {
    const grp = allGroups.find((g) => g.id === groupId);
    if (!grp) return toast.error("Group not found");
    if (grp.auto) {
      return toast.error(`"${grp.name}" is an auto group — membership is rule-driven`);
    }
    setAssigning(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/members/remove`, {
        user_ids: Array.from(selectedIds),
      });
      toast.success(
        data.removed > 0
          ? `Removed ${data.removed} user(s) from "${grp.name}"`
          : `No changes — none of the selected users were in "${grp.name}"`
      );
      setShowRemovePicker(false);
      setSelectedIds(new Set());
      try {
        const ids = users.map((u) => u.id);
        const { data: gm } = await api.post("/groups/user-memberships", { user_ids: ids });
        setGroupsByUser(gm?.memberships || {});
      } catch { /* non-critical */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally { setAssigning(false); }
  };

  const sorted = useMemo(() => {
    const acc = SORT_ACCESSORS[sort.field] || SORT_ACCESSORS.join_date;
    const arr = [...users];
    arr.sort((a, b) => {
      const av = acc(a); const bv = acc(b);
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [users, sort]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/trial-users/sync");
      toast.success(`Synced ${data.synced} users`);
      load();
    } catch { toast.error("Sync failed"); } finally { setSyncing(false); }
  };

  const exportXlsx = async () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (statusFilter) p.set("status", statusFilter);
    if (stateFilter) p.set("state", stateFilter);
    if (bucket) p.set("bucket", bucket);
    const token = localStorage.getItem("phc_token");
    const res = await fetch(`${API_BASE}/exports/trial-users.xlsx?${p}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return toast.error("Export failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "users.xlsx"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded");
  };

  return (
    <div data-testid={USERS.root} className="p-6 lg:p-10 max-w-[1720px]">
      {/* v29.14 — Multi-group conflict dialog (rendered above everything) */}
      {conflictDialog && (
        <div
          data-testid="users-group-conflict-modal"
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !assigning && setConflictDialog(null)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-2xl border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-slate-200">
              <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-600 mb-1">
                Heads up
              </div>
              <h2 className="font-heading text-xl font-bold tracking-tight">
                {conflictDialog.conflicts.length} of {selectedIds.size} user(s) are already in another group
              </h2>
              <p className="text-xs text-slate-600 mt-1">
                Proposed group: <b>&quot;{conflictDialog.groupName}&quot;</b>. Choose how you want to handle these overlaps.
              </p>
            </div>
            <div className="px-5 py-4 max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="text-[9px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
                  <tr>
                    <th className="text-left py-2 font-semibold">User</th>
                    <th className="text-left py-2 font-semibold">Currently in</th>
                  </tr>
                </thead>
                <tbody>
                  {conflictDialog.conflicts.map((c) => (
                    <tr
                      key={c.user_id}
                      data-testid={`users-conflict-row-${c.user_id}`}
                      className="border-b border-slate-50 last:border-0"
                    >
                      <td className="py-2 pr-3 font-medium">{c.name || c.user_id.slice(0, 8)}</td>
                      <td className="py-2">
                        <div className="flex flex-wrap gap-1">
                          {c.current_groups.map((g) => (
                            <span
                              key={g.id}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${
                                g.auto
                                  ? "bg-blue-50 border-blue-200 text-blue-700"
                                  : "bg-slate-50 border-slate-200 text-slate-700"
                              }`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full bg-${g.color || "slate"}-500`} />
                              {g.name}
                              {g.auto && <span className="text-[9px] opacity-70">auto</span>}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 rounded-b-lg">
              <button
                data-testid="users-conflict-add-anyway"
                onClick={() => _performAddOrMove(conflictDialog.groupId, conflictDialog.groupName, "add")}
                disabled={assigning}
                className="flex-1 text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 px-4 py-2 rounded-md disabled:opacity-70"
              >
                {assigning ? "Working…" : "Add anyway (keep all memberships)"}
              </button>
              <button
                data-testid="users-conflict-move-here"
                onClick={() => _performAddOrMove(conflictDialog.groupId, conflictDialog.groupName, "move")}
                disabled={assigning}
                className="flex-1 text-xs font-semibold bg-amber-500 text-white hover:bg-amber-600 px-4 py-2 rounded-md disabled:opacity-70"
                title="Removes conflicting users from all their OTHER static groups (dynamic groups stay — they are rule-driven), then adds everyone selected here."
              >
                {assigning ? "Working…" : "Move here (remove from other static groups)"}
              </button>
              <button
                data-testid="users-conflict-dont-add"
                onClick={() => _performAddOrMove(
                  conflictDialog.groupId,
                  conflictDialog.groupName,
                  "add",
                  conflictDialog.cleanUserIds || [],
                )}
                disabled={assigning}
                className="flex-1 text-xs font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300 px-4 py-2 rounded-md disabled:opacity-70"
                title="Skip the conflicting users. Add only the users who are not in any other group."
              >
                {assigning
                  ? "Working…"
                  : `Don't add conflicting (${conflictDialog.conflicts.length}) · add ${conflictDialog.cleanUserIds?.length || 0} clean`}
              </button>
              <button
                data-testid="users-conflict-cancel"
                onClick={() => setConflictDialog(null)}
                disabled={assigning}
                className="text-xs font-semibold text-slate-500 hover:text-slate-800 px-3 py-2 disabled:opacity-70"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">People</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            Users
            <HintIcon
              testId="users-page-hint"
              hint={
                <>
                  Synced from PrepHacker production DB via{" "}
                  <code className="font-mono text-[10.5px] bg-slate-100 px-1 rounded">MasterDataProvider</code>.<br /><br />
                  <b>Buckets</b> —
                  <br /><b>Day 1 / Day 2 / Day 3</b>: trial users by elapsed hours since signup.
                  <br /><b className="text-emerald-700">Active</b>: past 72h, unpaid, logged in within 7 days.
                  <br /><b className="text-slate-700">Inactive</b>: past 72h, unpaid, no login for 7+ days.
                  <br /><b className="text-indigo-700">Paid</b>: converted subscribers.<br /><br />
                  Click any tag chip to filter — or use the search + status filters below.
                </>
              }
            />
          </h1>
          {tag && (
            <div className="mt-3 inline-flex items-center gap-2 bg-purple-50 border border-purple-200 text-purple-900 rounded-md px-3 py-1.5 text-xs">
              <Tag className="h-3.5 w-3.5" />
              <span>Filtered by label:</span>
              <span className="font-bold uppercase tracking-widest text-[10px]">{tag}</span>
              <button
                data-testid="users-clear-tag-filter"
                onClick={() => {
                  const p = new URLSearchParams(params);
                  p.delete("tag");
                  setParams(p);
                }}
                className="ml-1 text-purple-700 hover:text-purple-900"
                title="Clear label filter"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="users-import-btn"
            onClick={() => setShowImport(true)}
            className="inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-2 rounded-md hover:bg-slate-50 transition-colors duration-150"
          >
            <Upload className="h-3.5 w-3.5" /> Import
          </button>
          <button
            onClick={exportXlsx}
            data-testid="users-export-btn"
            className="inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-2 rounded-md hover:bg-slate-50 transition-colors duration-150"
          >
            <Download className="h-3.5 w-3.5" /> Export Excel
          </button>
          <button
            data-testid={USERS.syncBtn}
            onClick={sync}
            disabled={syncing}
            className="inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-2 rounded-md hover:bg-slate-50 transition-colors duration-150"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
            Sync now
          </button>
        </div>
      </div>

      {/* Bucket filter chips */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mr-1">Filter by bucket</span>
        {[
          { key: "", label: "All" },
          { key: "day1", label: "Day 1 (0-24h)" },
          { key: "day2", label: "Day 2 (24-48h)" },
          { key: "day3", label: "Day 3 (48-72h)" },
          { key: "active", label: "Active" },
          { key: "inactive", label: "Inactive" },
          { key: "paid", label: "Paid" },
        ].map((b) => {
          const on = (bucket || "") === b.key;
          return (
            <button
              key={b.key || "all"}
              data-testid={`users-bucket-chip-${b.key || "all"}`}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (b.key) next.set("bucket", b.key); else next.delete("bucket");
                setParams(next);
              }}
              className={`text-[11px] font-semibold uppercase tracking-widest px-3 py-1.5 rounded-md border transition-colors duration-150 ${
                on
                  ? "bg-orange-600 text-white border-orange-600"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {b.label}
            </button>
          );
        })}
      </div>

      {/* Import filter chips — quick access to import-related segments. */}
      <ImportFilters params={params} setParams={setParams} activeTag={tag} users={users} />

      {/* v29.12 Phase 4 — Promo drill-down filter banner. */}
      {promo && (
        <div data-testid="users-promo-filter-banner" className="mb-4 flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-orange-800">Promo drill-down</span>
          <span className="text-sm font-mono font-bold text-orange-700">{promo}</span>
          <span className="text-xs text-orange-900/70 flex-1">
            Showing users who received this promo code. Columns show funnel stage,
            attributed agent/cadence, and payment status.
          </span>
          <button
            data-testid="users-promo-filter-clear"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.delete("promo");
              setParams(next);
            }}
            className="text-xs font-semibold text-orange-700 hover:text-orange-900 underline"
          >Clear</button>
        </div>
      )}

      {escalated && (
        <div className="mb-4 inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-red-700">Filter</span>
          <span className="text-sm font-semibold text-red-900">Escalated only</span>
          <button
            onClick={() => { const n = new URLSearchParams(params); n.delete("escalated"); setParams(n); }}
            className="text-red-700 hover:text-red-900 ml-1"
          ><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {dnd && (
        <div data-testid="users-active-filter-dnd" className="mb-4 inline-flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 mr-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-rose-700">Filter</span>
          <span className="text-sm font-semibold text-rose-900">DND only</span>
          <button
            data-testid="users-active-filter-dnd-clear"
            onClick={() => { const n = new URLSearchParams(params); n.delete("dnd"); setParams(n); }}
            className="text-rose-700 hover:text-rose-900 ml-1"
          ><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {aiPaused && (
        <div data-testid="users-active-filter-ai-paused" className="mb-4 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mr-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-700">Filter</span>
          <span className="text-sm font-semibold text-amber-900">AI paused only</span>
          <button
            data-testid="users-active-filter-ai-paused-clear"
            onClick={() => { const n = new URLSearchParams(params); n.delete("ai_paused"); setParams(n); }}
            className="text-amber-700 hover:text-amber-900 ml-1"
          ><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {liveSalesMode && (
        <div data-testid="users-active-filter-live-sales" className="mb-4 inline-flex items-center gap-2 bg-orange-50 border border-orange-300 rounded-md px-3 py-2 mr-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-orange-700">Filter</span>
          <span className="text-sm font-semibold text-orange-900">🔥 Live Sales Mode only</span>
          <button
            data-testid="users-active-filter-live-sales-clear"
            onClick={() => { const n = new URLSearchParams(params); n.delete("live_sales_mode"); setParams(n); }}
            className="text-orange-700 hover:text-orange-900 ml-1"
          ><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {hasFollowup && (
        <div data-testid="users-active-filter-followup" className="mb-4 inline-flex items-center gap-2 bg-slate-100 border border-slate-300 rounded-md px-3 py-2 mr-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Filter</span>
          <span className="text-sm font-semibold text-slate-800">Follow-up scheduled</span>
          <button
            data-testid="users-active-filter-followup-clear"
            onClick={() => { const n = new URLSearchParams(params); n.delete("has_followup"); setParams(n); }}
            className="text-slate-700 hover:text-slate-900 ml-1"
          ><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {(city || region) && (
        <div data-testid="users-active-filter-geo" className="mb-4 inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2 mr-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-indigo-700">Location</span>
          <span className="text-sm font-semibold text-indigo-900">{city || region}</span>
          <button
            onClick={() => { const n = new URLSearchParams(params); n.delete("city"); n.delete("region"); setParams(n); }}
            className="text-indigo-700 hover:text-indigo-900 ml-1"
          ><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input
            data-testid={USERS.searchInput}
            placeholder="Search name, email, phone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
          />
        </div>
        <select
          data-testid={USERS.statusFilter}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
        >
          <option value="">All statuses</option>
          <option value="trial">Trial</option>
          <option value="paid">Paid</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          data-testid="users-activity-filter"
          value={
            groupFilter
              ? `group:${groupFilter}`
              : statusFilter === "deleted"
              ? "cohort:deleted"
              : tag === "unregistered_wa_inbound"
              ? "cohort:wa_prospect"
              : stateFilter
          }
          onChange={(e) => {
            const v = e.target.value;
            const next = new URLSearchParams(params);
            // Clear cohort / group / state params first — this dropdown is the
            // single source of truth for "activity / cohort / group" filters.
            next.delete("state");
            next.delete("group");
            if (statusFilter === "deleted") { next.delete("status"); setStatusFilter(""); }
            if (tag === "unregistered_wa_inbound") next.delete("tag");
            if (v === "cohort:deleted") {
              next.set("status", "deleted");
              setStatusFilter("deleted");
              setStateFilter("");
            } else if (v === "cohort:wa_prospect") {
              next.set("tag", "unregistered_wa_inbound");
              setStateFilter("");
            } else if (v.startsWith("group:")) {
              // v29.14 — filter to users in a specific group (static OR dynamic).
              next.set("group", v.slice("group:".length));
              setStateFilter("");
            } else if (v) {
              next.set("state", v);
              setStateFilter(v);
            } else {
              setStateFilter("");
            }
            setParams(next);
          }}
          className="px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
        >
          <option value="">All activity</option>
          <option value="active_trial">Active in trial</option>
          <option value="inactive_trial">Inactive in trial</option>
          <option value="expired">Trial expired (no pay)</option>
          <option value="cancelled">Unsubscribed</option>
          <option disabled>──────────</option>
          <option value="cohort:deleted" data-testid="users-activity-option-deleted">Deleted users</option>
          <option value="cohort:wa_prospect" data-testid="users-activity-option-wa-prospect">WA Prospects (unregistered)</option>
          {allGroups.length > 0 && <option disabled>── Groups ──</option>}
          {allGroups.map((g) => (
            <option
              key={g.id}
              value={`group:${g.id}`}
              data-testid={`users-activity-option-group-${g.id}`}
            >
              {g.auto ? "◆ " : "● "}{g.name}
            </option>
          ))}
        </select>
        <div className="text-xs text-slate-500 tabular">{sorted.length} shown</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {/* Sticky bulk-action bar — only appears when rows are selected */}
        {selectedIds.size > 0 && (
          <div
            data-testid="users-bulk-bar"
            className="sticky top-0 z-10 bg-indigo-600 text-white px-4 py-2.5 flex items-center gap-3 border-b border-indigo-800"
          >
            <span className="text-xs font-bold uppercase tracking-widest">
              {selectedIds.size} selected
            </span>
            <span className="opacity-70 text-xs">of {sorted.length}</span>
            <div className="flex-1" />
            <div className="relative">
              <button
                data-testid="users-bulk-add-to-group-btn"
                onClick={() => setShowGroupPicker((s) => !s)}
                disabled={assigning}
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white text-indigo-700 hover:bg-indigo-50 px-3 py-1.5 rounded-md disabled:opacity-70"
              >
                <Users2 className="h-3.5 w-3.5" /> Add to group
              </button>
              {showGroupPicker && (
                <div
                  data-testid="users-bulk-group-picker"
                  className="absolute right-0 top-full mt-2 bg-white text-slate-900 border border-slate-200 rounded-md shadow-lg w-64 max-h-72 overflow-y-auto z-20"
                >
                  <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 border-b border-slate-100">
                    Pick a static group
                  </div>
                  {allGroups.filter((g) => !g.auto).length === 0 ? (
                    <div className="px-3 py-4 text-xs text-slate-500 italic">
                      No static groups yet. Create one in the Groups page.
                    </div>
                  ) : (
                    allGroups.filter((g) => !g.auto).map((g) => (
                      <button
                        key={g.id}
                        data-testid={`users-bulk-group-option-${g.id}`}
                        onClick={() => addSelectedToGroup(g.id)}
                        disabled={assigning}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 disabled:opacity-60"
                      >
                        <span className={`h-2 w-2 rounded-full bg-${g.color || "slate"}-500`}></span>
                        <span className="flex-1 truncate">{g.name}</span>
                        <span className="text-[10px] text-slate-500">{g.member_count ?? 0}</span>
                      </button>
                    ))
                  )}
                  {allGroups.filter((g) => g.auto).length > 0 && (
                    <div className="border-t border-slate-100 px-3 py-2 text-[10px] text-slate-400 italic">
                      Auto groups can&apos;t be edited manually.
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* v29.14 — Remove-from-group bulk action */}
            <div className="relative">
              <button
                data-testid="users-bulk-remove-from-group-btn"
                onClick={() => { setShowRemovePicker((s) => !s); setShowGroupPicker(false); }}
                disabled={assigning}
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white text-indigo-700 hover:bg-red-50 hover:text-red-700 px-3 py-1.5 rounded-md disabled:opacity-70"
              >
                Remove from group
              </button>
              {showRemovePicker && (
                <div
                  data-testid="users-bulk-remove-picker"
                  className="absolute right-0 top-full mt-2 bg-white text-slate-900 border border-slate-200 rounded-md shadow-lg w-64 max-h-72 overflow-y-auto z-20"
                >
                  <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 border-b border-slate-100">
                    Remove from which static group?
                  </div>
                  {allGroups.filter((g) => !g.auto).length === 0 ? (
                    <div className="px-3 py-4 text-xs text-slate-500 italic">
                      No static groups configured.
                    </div>
                  ) : (
                    allGroups.filter((g) => !g.auto).map((g) => (
                      <button
                        key={g.id}
                        data-testid={`users-bulk-remove-option-${g.id}`}
                        onClick={() => removeSelectedFromGroup(g.id)}
                        disabled={assigning}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 flex items-center gap-2 disabled:opacity-60"
                      >
                        <span className={`h-2 w-2 rounded-full bg-${g.color || "slate"}-500`}></span>
                        <span className="flex-1 truncate">{g.name}</span>
                        <span className="text-[10px] text-slate-500">{g.member_count ?? 0}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <button
              data-testid="users-bulk-clear-btn"
              onClick={() => setSelectedIds(new Set())}
              className="text-xs font-semibold opacity-80 hover:opacity-100 hover:underline"
            >
              Clear
            </button>
          </div>
        )}
        <table data-testid={USERS.table} className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="w-10 px-3 py-3">
                <input
                  data-testid="users-select-all"
                  type="checkbox"
                  checked={sorted.length > 0 && selectedIds.size === sorted.length}
                  ref={(el) => {
                    if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < sorted.length;
                  }}
                  onChange={() => toggleAllVisible(selectedIds.size === sorted.length && sorted.length > 0)}
                  onClick={(e) => e.stopPropagation()}
                  className="h-3.5 w-3.5 accent-indigo-600 cursor-pointer"
                  title="Select all visible users"
                />
              </th>
              <SortHeader label="Name" field="name" sort={sort} setSort={setSort} />
              <SortHeader label="Exam" field="exam" sort={sort} setSort={setSort} />
              <SortHeader label="Location" field="city" sort={sort} setSort={setSort} />
              <SortHeader label="Joined" field="join_date" sort={sort} setSort={setSort} hint="When the user signed up on PrepHacker" />
              <SortHeader label="Last login" field="last_login" sort={sort} setSort={setSort} hint="Most recent login recorded in Master DB" />
              <SortHeader label="Trial Left" field="trial_hours_left" sort={sort} setSort={setSort} hint="Hours remaining in 3-day trial" />
              <SortHeader label="Score" field="conversion_score" sort={sort} setSort={setSort} hint="Hybrid conversion score (0-100)" />
              <th
                className="text-left px-4 py-3 font-semibold whitespace-nowrap"
                title="Outbound sent · inbound received (all channels)"
              >Messages</th>
              <SortHeader label="State" field="user_state" sort={sort} setSort={setSort} />
              <th
                className="text-left px-4 py-3 font-semibold whitespace-nowrap"
                title="When AI will next act — reply to an inbound (10s target) OR fire the next cadence step. Blocked states shown if DND / paused / cadence off."
              >Next AI</th>
              <th className="text-left px-4 py-3 font-semibold">Tags</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={13} className="p-8 text-center text-slate-500 text-sm">Loading…</td></tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={13} className="p-8 text-center text-slate-500 text-sm">No users match your filters.</td></tr>
            )}
            {sorted.map((u) => (
              <tr
                key={u.id}
                data-testid={`${USERS.rowPrefix}${u.id}`}
                onClick={() => nav(`/users/${u.id}`, { state: { from: `/users${window.location.search || ""}` } })}
                className={`border-b border-slate-100 last:border-0 cursor-pointer transition-colors duration-150 ${
                  u.escalated
                    ? "bg-red-50/70 hover:bg-red-100 border-l-4 border-l-red-500"
                    : selectedIds.has(u.id)
                      ? "bg-indigo-50/70 hover:bg-indigo-100"
                      : "hover:bg-slate-50"
                }`}
              >
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    data-testid={`users-row-select-${u.id}`}
                    type="checkbox"
                    checked={selectedIds.has(u.id)}
                    onChange={() => toggleOne(u.id)}
                    className="h-3.5 w-3.5 accent-indigo-600 cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="font-semibold flex items-center gap-1.5 flex-wrap">
                    {u.live_sales_mode && (
                      <Flame
                        className="h-3.5 w-3.5 text-orange-600 animate-pulse"
                        title="🔥 In Live Sales Mode"
                        data-testid={`live-sales-flame-${u.id}`}
                      />
                    )}
                    {u.name}
                    {u.escalated && (
                      <span
                        data-testid={`escalated-pill-${u.id}`}
                        className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-600 text-white border border-red-700"
                        title="This user was escalated to a human. Admin must clear the flag from the user detail page."
                      >
                        <AlertTriangle className="h-2.5 w-2.5" /> Escalated
                      </span>
                    )}
                    {u.dnd && <BellOff className="h-3 w-3 text-red-500" title="Do Not Contact" />}
                    {u.notes && <StickyNote className="h-3 w-3 text-amber-500" title={u.notes} />}
                    {u.next_followup_at && !u.live_sales_mode && (
                      <Clock className="h-3 w-3 text-slate-400" title="Follow-up scheduled" />
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                  {/* v29.15 — IDs strip (Connect + Master DB), click to copy */}
                  <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`users-row-ids-${u.id}`}>
                    <IdChip label="Connect" value={u.id} testId={`users-row-id-connect-${u.id}`} />
                    <IdChip label="Master" value={u.external_id} testId={`users-row-id-external-${u.id}`} />
                  </div>
                  {promo && u.promo_attribution && (
                    <div data-testid={`users-row-promo-attr-${u.id}`} className="flex flex-wrap gap-1 mt-1 items-center">
                      <span className={`inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                        u.promo_attribution.purchased ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : u.promo_attribution.current_stage === "PAY_NOW_CLICKED" || u.promo_attribution.current_stage === "RAZORPAY_OPENED" ? "bg-amber-50 text-amber-800 border-amber-200"
                        : u.promo_attribution.current_stage === "PAYMENT_FAILED" || u.promo_attribution.current_stage === "PAYMENT_CANCELLED" ? "bg-rose-50 text-rose-700 border-rose-200"
                        : "bg-indigo-50 text-indigo-700 border-indigo-200"
                      }`}>
                        {(u.promo_attribution.current_stage || "GENERATED").replace(/_/g, " ").toLowerCase()}
                      </span>
                      {u.promo_attribution.purchased && u.promo_attribution.payment_amount != null && (
                        <span className="text-[9px] font-semibold text-emerald-700 tabular-nums">₹{u.promo_attribution.payment_amount}</span>
                      )}
                      {u.promo_attribution.channel && (
                        <span className="text-[9px] uppercase tracking-widest text-slate-500">via {u.promo_attribution.channel}</span>
                      )}
                      {u.promo_attribution.cadence_id && (
                        <span className="text-[9px] font-mono text-slate-500" title="Cadence">
                          cad {u.promo_attribution.cadence_id.slice(0, 6)}…
                        </span>
                      )}
                    </div>
                  )}
                  {(groupsByUser[u.id] || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1" data-testid={`users-row-groups-${u.id}`}>
                      {(groupsByUser[u.id] || []).slice(0, 3).map((g) => (
                        <span
                          key={g.id}
                          onClick={(e) => { e.stopPropagation(); nav(`/groups`); }}
                          title={`Group: ${g.name}${g.system ? " (system)" : ""}`}
                          className={`inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border bg-${g.color || "slate"}-50 text-${g.color || "slate"}-700 border-${g.color || "slate"}-200`}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full bg-${g.color || "slate"}-500`}></span>
                          {g.name}
                        </span>
                      ))}
                      {(groupsByUser[u.id] || []).length > 3 && (
                        <span className="text-[9px] text-slate-400 self-center">+{(groupsByUser[u.id] || []).length - 3}</span>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-700">{u.exam || "—"}</td>
                <td className="px-4 py-3 text-slate-700">
                  <div>{u.city}</div>
                  <div className="text-xs text-slate-500">{u.state}, {u.country}</div>
                </td>
                <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                  <div className="text-xs tabular">{fmtDT(u.join_date)}</div>
                  <div className="text-[10px] text-slate-500">{relative(u.join_date)}</div>
                </td>
                <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                  <div className="text-xs tabular">{fmtDT(u.last_login)}</div>
                  <div className="text-[10px] text-slate-500">{u.last_login ? relative(u.last_login) : "never"}</div>
                </td>
                <td className="px-4 py-3 tabular">
                  {u.subscription_status === "trial" ? (
                    <span className={`font-semibold ${u.trial_hours_left < 12 ? "text-red-600" : u.trial_hours_left < 36 ? "text-orange-600" : "text-emerald-600"}`}>
                      {u.trial_hours_left}h
                    </span>
                  ) : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="inline-flex items-center gap-1 text-xs">
                    <Zap className="h-3 w-3 text-orange-500" />
                    <span className="font-mono font-semibold tabular">{u.conversion_score}</span>
                  </div>
                </td>
                <td className="px-4 py-3" data-testid={`users-row-msg-counts-${u.id}`}>
                  <div className="flex items-center gap-2 text-xs tabular whitespace-nowrap">
                    <span
                      className="inline-flex items-center gap-1 text-slate-700"
                      title="Total outbound messages sent (AI + manual, all channels)"
                    >
                      <Send className="h-3 w-3 text-emerald-600" />
                      <span className="font-mono font-semibold">{msgCounts[u.id]?.sent ?? 0}</span>
                    </span>
                    <span className="text-slate-300">·</span>
                    <span
                      className={`inline-flex items-center gap-1 ${((msgCounts[u.id]?.received) || 0) > 0 ? "text-indigo-700" : "text-slate-400"}`}
                      title="Total inbound replies received from this user"
                    >
                      <MessageSquareReply className="h-3 w-3" />
                      <span className="font-mono font-semibold">{msgCounts[u.id]?.received ?? 0}</span>
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${STATUS_COLORS[u.subscription_status] || STATUS_COLORS.expired}`}>
                    {STATE_LABELS[u.user_state] || u.subscription_status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <NextActionPill na={nextActions[u.id]} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {orderTagsForDisplay(u.tags).slice(0, 4).map((rowTag) => {
                      const auto = isAutoTag(rowTag);
                      // Shorten noisy batch tags for display (e.g. "import_batch_20260217_143022" → "batch 02-17 14:30").
                      const label = rowTag.startsWith("import_batch_")
                        ? "batch " + rowTag.replace("import_batch_", "").replace(/^\d{4}(\d{2})(\d{2})_(\d{2})(\d{2}).*$/, "$1-$2 $3:$4")
                        : rowTag;
                      return (
                        <button
                          key={rowTag}
                          onClick={(e) => {
                            e.stopPropagation();
                            const p = new URLSearchParams(params);
                            p.set("tag", rowTag);
                            setParams(p);
                          }}
                          title={`Filter by "${rowTag}"`}
                          className={`text-[10px] px-1.5 py-0.5 rounded font-semibold border transition-colors duration-150 ${
                            tag === rowTag
                              ? "bg-purple-600 text-white border-purple-600"
                              : auto
                                ? "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                                : "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                    {u.tags && u.tags.length > 4 && (
                      <span className="text-[10px] text-slate-400 self-center" title={u.tags.join(", ")}>+{u.tags.length - 4}</span>
                    )}
                  </div>
                </td>
                <td className="pr-3"><ChevronRight className="h-4 w-4 text-slate-400" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ImportUsersModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => {
          // Refresh the user list after a successful import
          load();
        }}
      />
    </div>
  );
}

/**
 * Row of quick filters for imported users. Shows counts pulled from the
 * currently-loaded `users` list (client-side, no extra API calls) so the
 * operator can instantly see "how many need phone cleanup" etc.
 * Also renders a dropdown of recent import batches + a dedupe'd list of
 * every manual (executive-chosen) tag the operator applied during imports.
 */
function ImportFilters({ params, setParams, activeTag, users }) {
  // Fixed pins with counts derived from the current view.
  const counts = useMemo(() => {
    const c = { imported: 0, phone_incomplete: 0, exam_unknown: 0 };
    (users || []).forEach((u) => {
      (u.tags || []).forEach((t) => { if (c[t] !== undefined) c[t] += 1; });
    });
    return c;
  }, [users]);

  // All manual tags currently used + recent batch tags (last 5 unique).
  const { manualTags, batchTags } = useMemo(() => {
    const manual = new Map();
    const batches = new Map();
    (users || []).forEach((u) => {
      (u.tags || []).forEach((t) => {
        if (t.startsWith("import_batch_")) {
          batches.set(t, (batches.get(t) || 0) + 1);
        } else if (!AUTO_TAG_LITERALS.has(t)) {
          manual.set(t, (manual.get(t) || 0) + 1);
        }
      });
    });
    const sortDesc = (a, b) => b[1] - a[1];
    return {
      manualTags: [...manual.entries()].sort(sortDesc).slice(0, 8),
      batchTags: [...batches.entries()].sort((a, b) => b[0].localeCompare(a[0])).slice(0, 5),
    };
  }, [users]);

  const applyTag = (t) => {
    const p = new URLSearchParams(params);
    if (t) p.set("tag", t); else p.delete("tag");
    setParams(p);
  };

  // Each pin definition — tagName + label + colour classes for on/off states.
  const PINS = [
    { tagName: "imported",         label: "Imported",     on: "bg-slate-800 text-white border-slate-800",   off: "bg-white text-slate-700 border-slate-200 hover:bg-slate-50" },
    { tagName: "phone_incomplete", label: "Phone missing", on: "bg-amber-600 text-white border-amber-600",  off: "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100" },
    { tagName: "exam_unknown",     label: "Exam missing",  on: "bg-slate-600 text-white border-slate-600",  off: "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100" },
  ];

  const hasAny = counts.imported > 0 || counts.phone_incomplete > 0 || counts.exam_unknown > 0 || manualTags.length > 0 || batchTags.length > 0;
  if (!hasAny) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mr-1">Import filters</span>
      {PINS.map((p) => {
        // Only render pins that have at least one user, except "imported" which
        // is always visible so operators can rapidly view "all imports".
        if (counts[p.tagName] === 0 && p.tagName !== "imported") return null;
        const on = activeTag === p.tagName;
        return (
          <button
            key={p.tagName}
            data-testid={`users-import-filter-${p.tagName}`}
            onClick={() => applyTag(on ? "" : p.tagName)}
            className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest px-3 py-1.5 rounded-md border transition-colors duration-150 ${on ? p.on : p.off}`}
          >
            {p.label}
            {counts[p.tagName] > 0 && (
              <span className={`font-mono tabular ${on ? "opacity-90" : "opacity-70"}`}>({counts[p.tagName]})</span>
            )}
          </button>
        );
      })}
      {batchTags.length > 0 && (
        <select
          data-testid="users-import-batch-select"
          value={activeTag && activeTag.startsWith("import_batch_") ? activeTag : ""}
          onChange={(e) => applyTag(e.target.value)}
          className="text-[11px] font-semibold px-2 py-1.5 rounded-md border border-slate-200 bg-white focus:border-orange-500 outline-none"
        >
          <option value="">Recent batches…</option>
          {batchTags.map(([t, n]) => (
            <option key={t} value={t}>
              {t.replace(/^import_batch_/, "").replace(/^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2}).*$/, "$1-$2-$3 $4:$5")} ({n})
            </option>
          ))}
        </select>
      )}
      {manualTags.length > 0 && (
        <>
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 ml-2">Manual tags</span>
          {manualTags.map(([t, n]) => {
            const on = activeTag === t;
            return (
              <button
                key={t}
                data-testid={`users-manual-tag-${t}`}
                onClick={() => applyTag(on ? "" : t)}
                className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border font-mono transition-colors duration-150 ${
                  on
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
                }`}
              >
                {t} <span className="opacity-70 tabular">({n})</span>
              </button>
            );
          })}
        </>
      )}
    </div>
  );
}
